import i18n from "../i18n";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertTriangle, Camera, MapPin, Plus, Trash2, X, ImageIcon,
    CheckCircle2, CircleAlert, Timer, User, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
    { key: "low", label: "Low", color: "bg-slate-500/15 text-slate-700" },
    { key: "medium", label: "Medium", color: "bg-blue-500/15 text-blue-700" },
    { key: "high", label: "High", color: "bg-amber-500/15 text-amber-700" },
    { key: "urgent", label: "Urgent", color: "bg-rose-500/15 text-rose-700" },
];

const STATUSES = [
    { key: "reported", label: "Reported", color: "bg-rose-500/15 text-rose-700" },
    { key: "acknowledged", label: "Acknowledged", color: "bg-amber-500/15 text-amber-700" },
    { key: "in_progress", label: "In Progress", color: "bg-blue-500/15 text-blue-700" },
    { key: "resolved", label: "Resolved", color: "bg-emerald-500/15 text-emerald-700" },
    { key: "closed", label: "Closed", color: "bg-slate-500/15 text-slate-700" },
];

const priorityMeta = (k) => PRIORITIES.find((p) => p.key === k) || PRIORITIES[1];
const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];

async function readFileAsDataURL(file) {
    const compress = (dataUrl) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const maxSize = 1200;
            let { width, height } = img;
            if (width > maxSize || height > maxSize) {
                const scale = maxSize / Math.max(width, height);
                width *= scale; height *= scale;
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", 0.75));
        };
        img.src = dataUrl;
    });
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => resolve(await compress(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function IncidentDialog({ open, initial, categories, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    useEffect(() => {
        if (open) setDraft({ category: "broken_equipment", priority: "medium", photos: [], ...(initial || {}) });
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const useLocation = () => {
        if (!navigator.geolocation) return toast.error("Geolocation not supported");
        setBusy(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                upd("gps_lat", pos.coords.latitude);
                upd("gps_lng", pos.coords.longitude);
                toast.success("Location captured");
                setBusy(false);
            },
            (err) => {
                toast.error(`Location denied: ${err.message}`);
                setBusy(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const cur = draft.photos || [];
        const remaining = Math.max(0, 5 - cur.length);
        const chosen = files.slice(0, remaining);
        if (files.length > remaining) toast.warning(`Only ${remaining} more photos allowed (max 5)`);
        const dataUrls = await Promise.all(chosen.map(readFileAsDataURL));
        upd("photos", [...cur, ...dataUrls]);
        if (fileRef.current) fileRef.current.value = "";
    };

    const removePhoto = (idx) => upd("photos", (draft.photos || []).filter((_, i) => i !== idx));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="incident-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        {i18n.t("batch4.38bd21ec32a0")}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.t("batch4.929a5e45f98a")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">{i18n.t("batch4.a3c686e711e4")}</Label>
                            <Select value={draft.category} onValueChange={(v) => upd("category", v)}>
                                <SelectTrigger className="mt-1 h-10" data-testid="inc-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">{i18n.t("batch4.886cbff9d9df")}</Label>
                            <Select value={draft.priority} onValueChange={(v) => upd("priority", v)}>
                                <SelectTrigger className="mt-1 h-10" data-testid="inc-priority"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("batch4.768e0c1c6957")}</Label>
                        <Input value={draft.title ?? ""} onChange={(e) => upd("title", e.target.value)}
                               placeholder={i18n.t("batch4.175db723dfd6")} className="mt-1 h-10" data-testid="inc-title" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("batch4.55f8ebc805e6")}</Label>
                        <Textarea rows={4} value={draft.description ?? ""} onChange={(e) => upd("description", e.target.value)}
                                  placeholder={i18n.t("batch4.0f4895f5fbcd")} className="mt-1" data-testid="inc-desc" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("batch4.8754b869f855")}</Label>
                        <Input value={draft.location ?? ""} onChange={(e) => upd("location", e.target.value)}
                               placeholder={i18n.t("batch4.693622d7cad1")} className="mt-1 h-10" />
                    </div>
                    <div>
                        <div className="flex justify-between items-center">
                            <Label className="text-xs">{i18n.t("batch4.b3c75ecfcf4b")}</Label>
                            <Button type="button" variant="outline" size="sm" onClick={useLocation} disabled={busy} data-testid="inc-gps-btn">
                                <MapPin className="w-3.5 h-3.5 mr-1" />{i18n.t("batch4.0132375d9a57")}
                            </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            <Input type="number" step="0.000001" value={draft.gps_lat ?? ""} onChange={(e) => upd("gps_lat", e.target.value === "" ? null : Number(e.target.value))} placeholder={i18n.t("batch4.ced3905ec822")} className="h-10" />
                            <Input type="number" step="0.000001" value={draft.gps_lng ?? ""} onChange={(e) => upd("gps_lng", e.target.value === "" ? null : Number(e.target.value))} placeholder={i18n.t("batch4.6f9f011c5d36")} className="h-10" />
                        </div>
                    </div>
                    <div>
                        <div className="flex justify-between items-center">
                            <Label className="text-xs">{i18n.t("batch4.c28adc5def0e")}</Label>
                            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="inc-photo-btn">
                                <Camera className="w-3.5 h-3.5 mr-1" />{i18n.t("batch4.560a4bd0f479")}
                            </Button>
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onFiles} />
                        {(draft.photos || []).length > 0 && (
                            <div className="grid grid-cols-5 gap-2 mt-2">
                                {(draft.photos || []).map((src, i) => (
                                    <div key={i} className="relative group aspect-square">
                                        <img src={src} alt={`photo-${i}`} className="w-full h-full object-cover rounded-md border" />
                                        <button type="button" className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={() => removePhoto(i)}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("batch4.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.title || !draft.category} data-testid="inc-save-btn">
                        <AlertTriangle className="w-4 h-4 mr-2" />{i18n.t("batch4.ee45c30326b7")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function IncidentDetail({ id, open, onClose, onUpdated }) {
    const { user, isOwner } = useAuth();
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        if (open && id) api.get(`/incidents/${id}`).then((r) => setDetail(r.data));
    }, [open, id]);

    const setStatus = async (s) => {
        try {
            const r = await api.put(`/incidents/${id}`, { status: s });
            setDetail(r.data);
            onUpdated?.();
            toast.success(`Status → ${s}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    };
    const setPriority = async (p) => {
        try {
            const r = await api.put(`/incidents/${id}`, { priority: p });
            setDetail(r.data);
            onUpdated?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    };

    if (!detail) return null;
    const p = priorityMeta(detail.priority);
    const s = statusMeta(detail.status);
    const canManage = isOwner() || detail.reported_by_id === user?.id;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={`incident-detail-${id}`}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />{detail.title}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.t("batch4.e0637047e8b6")} {detail.reported_by_name} · {new Date(detail.created_at).toLocaleString()}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className={p.color}>{p.label} {i18n.t("batch4.3345867ecfee")}</Badge>
                        <Badge variant="outline" className={s.color}>{s.label}</Badge>
                        <Badge variant="outline">{(detail.category || "").replace("_", " ")}</Badge>
                    </div>
                    {detail.description && <p className="text-sm whitespace-pre-wrap">{detail.description}</p>}
                    {(detail.location || detail.gps_lat) && (
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {detail.location}
                            {detail.gps_lat != null && (
                                <a href={`https://www.google.com/maps?q=${detail.gps_lat},${detail.gps_lng}`}
                                   target="_blank" rel="noreferrer" className="underline">
                                    ({detail.gps_lat.toFixed(5)}, {detail.gps_lng.toFixed(5)})
                                </a>
                            )}
                        </div>
                    )}
                    {(detail.photos || []).length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                            {detail.photos.map((src, i) => (
                                <a key={i} href={src} target="_blank" rel="noreferrer">
                                    <img src={src} className="w-full aspect-square object-cover rounded-md border" alt={`p${i}`} />
                                </a>
                            ))}
                        </div>
                    )}
                    {detail.resolved_at && (
                        <div className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            {i18n.t("batch4.5258b0f1543f")} {detail.resolved_by_name} · {new Date(detail.resolved_at).toLocaleString()}
                        </div>
                    )}
                    {canManage && (
                        <div className="border-t pt-3 space-y-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{i18n.t("batch4.fb91e24fa52d")}</div>
                            <div className="flex flex-wrap gap-2">
                                {STATUSES.map((st) => (
                                    <Button key={st.key} size="sm" variant={detail.status === st.key ? "default" : "outline"}
                                            onClick={() => setStatus(st.key)} data-testid={`set-status-${st.key}`}>
                                        {st.label}
                                    </Button>
                                ))}
                            </div>
                            {isOwner() && (
                                <div className="flex flex-wrap gap-2">
                                    {PRIORITIES.map((pr) => (
                                        <Button key={pr.key} size="sm" variant={detail.priority === pr.key ? "default" : "outline"}
                                                onClick={() => setPriority(pr.key)}>
                                            {pr.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function KpiCard({ icon: Icon, label, value, tone = "primary" }) {
    const tones = {
        primary: "bg-primary/10 text-primary",
        rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
        amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
        blue: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    };
    return (
        <Card className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className="text-2xl font-bold leading-tight">{value}</div>
            </div>
        </Card>
    );
}

export default function IncidentsPage() {
    const { isOwner, can } = useAuth();
    const [tab, setTab] = useState("all");
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ kpis: {} });
    const [categories, setCategories] = useState([]);
    const [reportDlg, setReportDlg] = useState(false);
    const [detailId, setDetailId] = useState(null);

    const load = async () => {
        try {
            const [list, sum, cats] = await Promise.all([
                api.get("/incidents?limit=200"),
                api.get("/incidents/summary"),
                api.get("/incidents/categories"),
            ]);
            setItems(list.data || []);
            setSummary(sum.data || { kpis: {} });
            setCategories(cats.data || []);
        } catch (e) { toast.error("Failed to load incidents"); }
    };
    useEffect(() => { load(); }, []);

    const save = async (draft) => {
        try {
            await api.post("/incidents", draft);
            toast.success("Incident reported");
            setReportDlg(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Report failed"); }
    };

    const del = async (inc) => {
        if (!confirm(`Delete incident "${inc.title}"?`)) return;
        try { await api.delete(`/incidents/${inc.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const k = summary.kpis || {};
    const filtered = useMemo(() => {
        if (tab === "all") return items;
        if (tab === "open") return items.filter((i) => !["resolved", "closed"].includes(i.status));
        if (tab === "urgent") return items.filter((i) => i.priority === "urgent" && !["resolved", "closed"].includes(i.status));
        if (tab === "resolved") return items.filter((i) => ["resolved", "closed"].includes(i.status));
        return items;
    }, [items, tab]);

    return (
        <div className="space-y-6" data-testid="incidents-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3">
                        <AlertTriangle className="w-8 h-8 text-amber-600" />{i18n.t("batch4.1a940eca136c")}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {isOwner() ? "All incidents reported by your team." : "Your submitted incidents."}
                    </p>
                </div>
                {can("add") && (
                    <Button onClick={() => setReportDlg(true)} data-testid="new-incident-btn">
                        <Plus className="w-4 h-4 mr-2" />{i18n.t("batch4.38bd21ec32a0")}
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={CircleAlert} label={i18n.t("batch4.b25928c69902")} value={k.total || 0} tone="primary" />
                <KpiCard icon={Timer} label={i18n.t("batch4.cf9b77061f7b")} value={k.open || 0} tone={k.open > 0 ? "amber" : "primary"} />
                <KpiCard icon={AlertTriangle} label={i18n.t("batch4.3d7ad2b86103")} value={k.urgent_open || 0} tone={k.urgent_open > 0 ? "rose" : "primary"} />
                <KpiCard icon={CheckCircle2} label={i18n.t("batch4.d999aeb0545f")} value={k.resolved || 0} tone="emerald" />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="all" data-testid="tab-all">{i18n.t("batch4.6a72085653e4")} <Badge variant="secondary" className="ml-2 text-[10px]">{items.length}</Badge></TabsTrigger>
                    <TabsTrigger value="open" data-testid="tab-open">{i18n.t("batch4.cf9b77061f7b")} <Badge variant="secondary" className="ml-2 text-[10px]">{k.open || 0}</Badge></TabsTrigger>
                    <TabsTrigger value="urgent" data-testid="tab-urgent">{i18n.t("batch4.ecb26f46e394")} <Badge variant="secondary" className="ml-2 text-[10px]">{k.urgent_open || 0}</Badge></TabsTrigger>
                    <TabsTrigger value="resolved" data-testid="tab-resolved">{i18n.t("batch4.d999aeb0545f")} <Badge variant="secondary" className="ml-2 text-[10px]">{k.resolved || 0}</Badge></TabsTrigger>
                </TabsList>

                <TabsContent value={tab} className="mt-4">
                    {filtered.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">
                            {i18n.t("batch4.9f5c08dbfa48")} {can("add") && "Click 'Report Incident' to file one."}
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filtered.map((inc) => {
                                const p = priorityMeta(inc.priority);
                                const s = statusMeta(inc.status);
                                return (
                                    <Card key={inc.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                                          onClick={() => setDetailId(inc.id)} data-testid={`incident-card-${inc.id}`}>
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold leading-tight truncate">{inc.title}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5 capitalize">
                                                    {(inc.category || "").replace("_", " ")}
                                                </div>
                                            </div>
                                            <Badge variant="outline" className={`text-[10px] ${p.color}`}>{p.label}</Badge>
                                        </div>
                                        {inc.description && <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{inc.description}</div>}
                                        <div className="flex items-center justify-between mt-3">
                                            <Badge variant="outline" className={`text-[10px] ${s.color}`}>{s.label}</Badge>
                                            <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                                <User className="w-3 h-3" />
                                                {inc.reported_by_name || "?"}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t">
                                            <div className="text-[11px] text-muted-foreground">
                                                {new Date(inc.created_at).toLocaleDateString()}
                                            </div>
                                            {isOwner() && (
                                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(inc); }}>
                                                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                                </Button>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <IncidentDialog open={reportDlg} categories={categories} onSave={save} onClose={() => setReportDlg(false)} />
            {detailId && (
                <IncidentDetail id={detailId} open={!!detailId} onClose={() => setDetailId(null)} onUpdated={load} />
            )}
        </div>
    );
}
